"use client";

import { useTextDirectionContext } from "../../../helpers/TextDirectionContext";
import { SliderButton } from "../../SliderButton/SliderButton";
import { KnowledgeCard } from "../KnowledgeCard/KnowledgeCard";
import classNames from "classnames/bind";
import { EmblaCarouselType, EmblaOptionsType } from "embla-carousel";
import useEmblaCarousel from "embla-carousel-react";
import { useCallback, useEffect, useState } from "react";
import styles from "./KnowledgeRail.module.css";

const cx = classNames.bind(styles);

export function KnowledgeRail({
  slides,
  useCrhPadding = false,
}: {
  slides: {
    _uid: string;
    title: string;
    kicker: string;
    imageUrl: string;
    linkUrl?: string;
  }[];
  useCrhPadding?: boolean;
}) {
  const textDirection = useTextDirectionContext();

  const EMBLA_OPTIONS: EmblaOptionsType = {
    align: "start",
    direction: textDirection,
  };

  // Embla Carousel
  const [emblaRef, emblaApi] = useEmblaCarousel(EMBLA_OPTIONS);

  // Embla Next/Prev buttons
  const [prevBtnDisabled, setPrevBtnDisabled] = useState<boolean>(true);
  const [nextBtnDisabled, setNextBtnDisabled] = useState<boolean>(true);

  // Embla Prev button
  const onPrevButtonClick = useCallback(() => {
    if (!emblaApi) return;
    emblaApi.scrollPrev();
  }, [emblaApi]);

  // Embla Next button
  const onNextButtonClick = useCallback(() => {
    if (!emblaApi) return;
    emblaApi.scrollNext();
  }, [emblaApi]);

  // Embla onSelect
  const onSelect = useCallback((emblaApi: EmblaCarouselType) => {
    setPrevBtnDisabled(!emblaApi.canScrollPrev());
    setNextBtnDisabled(!emblaApi.canScrollNext());
  }, []);

  // Embla useEffect
  useEffect(() => {
    if (!emblaApi) return;

    onSelect(emblaApi);
    emblaApi.on("reInit", onSelect);
    emblaApi.on("select", onSelect);
  }, [emblaApi, onSelect]);

  return (
    <div
      className={cx("container")}
      {...(useCrhPadding && { "data-use-crh-padding": "true" })}
    >
      <div className={cx("embla")}>
        <div className={cx("carousel-nav")}>
          <SliderButton
            kind="previous"
            buttonKind="secondary"
            onClick={onPrevButtonClick}
            disabled={prevBtnDisabled}
            className={cx("nav-btn", "nav-btn--prev")}
          />
          <SliderButton
            kind="next"
            buttonKind="secondary"
            onClick={onNextButtonClick}
            disabled={nextBtnDisabled}
            className={cx("nav-btn", "nav-btn--next")}
          />
        </div>
        <div className={cx("embla-viewport")} ref={emblaRef}>
          <div className={cx("embla-container")}>
            {slides?.map((item, index) => (
              <div key={index} className={cx("embla-slide")}>
                <KnowledgeCard
                  key={item._uid}
                  imageUrl={item.imageUrl}
                  kicker={item.kicker}
                  title={item.title}
                  linkUrl={item.linkUrl}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
